import React, { useState, useEffect, forwardRef, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { format, parseISO } from 'date-fns';
import { getChartData, getChartOptions } from './utils/chart';
import type { ScenarioResult } from '../../types';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const ANIMATION_DURATION = 2000;

interface GrowthChartProps {
  results: ScenarioResult;
}

export const GrowthChart = forwardRef<ChartJS, GrowthChartProps>(({ results }, ref) => {
  const [animationProgress, setAnimationProgress] = useState(0);
  const animationRef = useRef<number>();

  useEffect(() => {
    const startTime = Date.now();
    
    const animate = () => {
      const currentTime = Date.now();
      const progress = Math.min((currentTime - startTime) / ANIMATION_DURATION, 1);
      setAnimationProgress(progress);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [results]);

  const chartData = getChartData(results, animationProgress);
  const chartOptions = getChartOptions(results);

  return (
    <div className="h-[400px] mb-6">
      <Line ref={ref} data={chartData} options={chartOptions} />
    </div>
  );
});

GrowthChart.displayName = 'GrowthChart';